import { Suspense, lazy } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useAuth } from '@clerk/clerk-react'
import { Toaster } from 'sonner'
import NotFound from '@/pages/NotFound'
import AppLayout from '@/components/layout/AppLayout'
import { ErrorBoundary } from '@/components/ErrorBoundary'
import { WorkspaceProvider } from '@/contexts/WorkspaceContext'
import { useHtmlLang } from '@/hooks/useHtmlLang'

const Landing = lazy(() => import('@/pages/Landing'))
const Privacy = lazy(() => import('@/pages/Privacy'))
const Terms = lazy(() => import('@/pages/Terms'))
const Dashboard = lazy(() => import('@/pages/Dashboard'))
const Upload = lazy(() => import('@/pages/Upload'))
const Review = lazy(() => import('@/pages/Review'))
const Settings = lazy(() => import('@/pages/Settings'))
const Workspaces = lazy(() => import('@/pages/Workspaces'))
const WorkspaceDetail = lazy(() => import('@/pages/WorkspaceDetail'))
const SharedReview = lazy(() => import('@/pages/SharedReview'))
const CompareContracts = lazy(() => import('@/pages/CompareContracts'))
const BatchUpload = lazy(() => import('@/pages/BatchUpload'))

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoaded } = useAuth()
  if (!isLoaded) return null
  if (!isSignedIn) return <Navigate to="/" replace />
  return <>{children}</>
}

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  )
}

export default function App() {
  useHtmlLang()
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <WorkspaceProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route element={<AppLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/upload"
                  element={
                    <ProtectedRoute>
                      <Upload />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/review/:id"
                  element={
                    <ProtectedRoute>
                      <Review />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <ProtectedRoute>
                      <Settings />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/workspaces"
                  element={
                    <ProtectedRoute>
                      <Workspaces />
                    </ProtectedRoute>
                  }
                />
                <Route path="/workspaces/:id"
                  element={
                    <ProtectedRoute>
                      <WorkspaceDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/compare"
                  element={
                    <ProtectedRoute>
                      <CompareContracts />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/batch-upload"
                  element={
                    <ProtectedRoute>
                      <BatchUpload />
                    </ProtectedRoute>
                  }
                />
                <Route path="/shared/:token" element={<SharedReview />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </WorkspaceProvider>
      </BrowserRouter>
    </ErrorBoundary>
  )
}

